import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../../../../../context/LanguageContext';
import '../style/services-section.css';
import { initializeServicesSection } from '../script/services-section';
import { initializeTitleAnimation } from '../script/title-animation';
import { initializeServicesMobileSlider } from '../script/services-mobile-slider';
import { SERVICES_DATA } from '../../../../../data/types/ServiceTypes';

const ServicesSection: React.FC = () => {
    const { t, language } = useLanguage();
    const containerRef = useRef<HTMLElement>(null);
    const swiperRef = useRef<HTMLDivElement>(null);
    const swiperInstanceRef = useRef<any>(null);

    // Type assertion for nested translation access
    const translation = t as any;
    const servicesTranslations = translation.SERVICES || t.SERVICES;

    useEffect(() => {
        if (!containerRef.current || swiperInstanceRef.current) {
            return;
        }

        // Initialize Swiper using the original script function
        initializeServicesSection(containerRef.current);
        swiperInstanceRef.current = (window as any).servicesSwiper;

        // Cleanup
        return () => {
            if ((window as any).servicesSwiper) {
                try {
                    (window as any).servicesSwiper.destroy(true, true);
                    (window as any).servicesSwiper = null;
                } catch (_) {
                }
            }
        };
    }, []);

    // Initialize title animation only once on first page load
    useEffect(() => {
        const hasInitialized = sessionStorage.getItem('services-titles-initialized');
        
        const timer = setTimeout(() => {
            if (!hasInitialized) {
            initializeTitleAnimation();
                sessionStorage.setItem('services-titles-initialized', 'true');
            } else {
                // On language change, just make titles visible without animation
                const titles = document.querySelectorAll('.services-one .title-animation');
                titles.forEach((title: Element) => {
                    const el = title as HTMLElement;
                    el.style.opacity = '1';
                });
            }
            
            // Refresh ScrollTrigger to recalculate positions
            if (typeof window !== 'undefined' && (window as any).ScrollTrigger) {
                (window as any).ScrollTrigger.refresh();
            }
        }, 300);
        
        return () => {
            clearTimeout(timer);
        };
    }, [language]); // Re-run when language changes

    // Initialize mobile slider for services
    useEffect(() => {
        if (!containerRef.current) {
            return;
        }

        // Initialize mobile slider if on mobile
        if (window.innerWidth <= 991) {
            setTimeout(() => {
                initializeServicesMobileSlider(containerRef.current);
            }, 500);
        }

        // Re-initialize on window resize (only if crossing mobile threshold)
        let wasMobile = window.innerWidth <= 991;
        const resizeHandler = () => {
            const isMobile = window.innerWidth <= 991;
            if (isMobile !== wasMobile) {
                wasMobile = isMobile;
                if (isMobile) {
                    setTimeout(() => {
                        initializeServicesMobileSlider(containerRef.current);
                    }, 100);
                }
            }
        };

        window.addEventListener('resize', resizeHandler);

        return () => {
            window.removeEventListener('resize', resizeHandler);
        };
    }, []);


    return (
        <section className="services-one" ref={containerRef}>
            <div className="container">
                <div className="section-title text-center sec-title-animation animation-style2">
                    <div className="section-title__tagline-box">
                        <div className="section-title__tagline-border">
                            <div className="section-title__shape-1">
                                <i className="section-title__circle"></i>
                            </div>
                        </div>
                        <h6 className="section-title__tagline">
                            {servicesTranslations?.tagline}
                        </h6>
                        <div className="section-title__tagline-border">
                            <div className="section-title__shape-2">
                                <i className="section-title__circle"></i>
                            </div>
                        </div>
                    </div>
                    <h2
                        className="section-title__title title-animation"
                        key={`services-title-${language}`}
                        dangerouslySetInnerHTML={{ __html: servicesTranslations?.title }}
                    ></h2>
                </div>
                <div 
                    ref={swiperRef}
                    className="swiper-container services-one__carousel"
                >
                    <div className="swiper-wrapper">
                        {SERVICES_DATA.map((service) => (
                            <div key={service.id} className="swiper-slide">
                                {/* Card links to the service page; separate "Peça orçamento" link per card (A5) */}
                                <div
                                    className="services-one__single"
                                    style={{ textDecoration: 'none', display: 'block' }}
                                >
                                    <Link
                                        to={service.route}
                                        className="services-one__img-box"
                                        style={{ cursor: 'pointer', textDecoration: 'none', display: 'block' }}
                                    >
                                        <div className="services-one__img">
                                            <img loading="lazy"
                                                src={service.imageSrc}
                                                alt={service.imageAlt}
                                            />
                                        </div>
                                        <div className="services-one__count">
                                            {String(service.id).padStart(2, '0')}
                                        </div>
                                    </Link>
                                    <div className="services-one__content">
                                        <div className="services-one__icon">
                                            <span className={service.iconClass}></span>
                                        </div>
                                        <h3 className="services-one__title">
                                            <Link to={service.route}>
                                                {servicesTranslations?.[service.titleKey]}
                                            </Link>
                                        </h3>
                                        <p className="services-one__text">
                                            {servicesTranslations?.[service.descKey]}
                                        </p>
                                        <Link
                                            to={`/orcamento?service=${service.budgetParam}`}
                                            className="services-one__quote-link"
                                        >
                                            {(t as any).BUTTONS?.requestQuote || 'Peça orçamento'}
                                            <span><i className="icon-next"></i></span>
                                        </Link>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                    {/* Pagination */}
                    <div id="swiper-dot-style1" className="swiper-pagination"></div>
                </div>
            </div>
        </section>
    );
};

export default ServicesSection;

